import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, apiGet, apiSend } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { newIdempotencyKey } from '../api/payments';
import { BonusSelectSheet } from '../components/BonusSelectSheet';

interface Campaign {
  code: string;
  campaign_type: string;
  title: string;
  description: string;
  reward_minor: number;
}

interface HistoryRow {
  code: string;
  title: string;
  amount_minor: number;
  created_at: string | null;
}

type PromoFilter = 'all' | 'welcome' | 'casino' | 'sports' | 'vip';

const FILTERS: PromoFilter[] = ['all', 'welcome', 'casino', 'sports', 'vip'];
const SHEET_KEY = 'vbb_bonus_sheet_seen';
const PROMO_CODES = [
  'invalid_code',
  'campaign_expired',
  'campaign_inactive',
  'campaign_not_started',
  'per_user_limit',
  'unique_user_limit',
  'usage_limit',
  'not_eligible',
  'minimum_deposit_not_satisfied',
  'budget_exhausted',
  'too_many_requests',
  'zero_reward',
];

export function BonusesPage() {
  const { t } = useTranslation(['common', 'nav']);
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [filter, setFilter] = useState<PromoFilter>('all');
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    void apiGet<Campaign[]>('/api/v1/bonuses/campaigns')
      .then((rows) => {
        setCampaigns(rows);
        if (user && rows.length > 0 && !sessionStorage.getItem(SHEET_KEY)) {
          setSheetOpen(true);
        }
      })
      .catch(() => setCampaigns([]));
    if (user) {
      void apiGet<HistoryRow[]>('/api/v1/bonuses/history')
        .then(setHistory)
        .catch(() => setHistory([]));
    }
  }, [user?.public_id]);

  const visible = useMemo(() => {
    if (filter === 'all') return campaigns;
    return campaigns.filter((c) => c.campaign_type.toLowerCase().includes(filter));
  }, [campaigns, filter]);

  const featured = visible[0] ?? null;
  const rest = featured ? visible.slice(1) : visible;

  function closeSheet() {
    sessionStorage.setItem(SHEET_KEY, '1');
    setSheetOpen(false);
  }

  function onSheetContinue(selected: string | null) {
    if (selected) setCode(selected);
    closeSheet();
  }

  function promoMessage(err: unknown): string {
    if (err instanceof ApiError) {
      const body = err.body as { detail?: string } | null;
      const detail = body?.detail;
      if (typeof detail === 'string' && PROMO_CODES.includes(detail)) {
        return t(`common:promo_${detail}`);
      }
      if (err.status === 429) return t('common:promo_too_many_requests');
    }
    if (!user) return t('common:promoLoginRequired');
    return t('common:promo_redeem_failed');
  }

  async function onRedeem(event: FormEvent) {
    event.preventDefault();
    setOk(false);
    if (!user) {
      setMsg(t('common:promoLoginRequired'));
      return;
    }
    try {
      const res = await apiSend<{ amount_minor: number }>('POST', '/api/v1/bonuses/redeem', {
        code,
        idempotency_key: newIdempotencyKey('promo'),
      });
      const amount = (res.amount_minor / 100).toFixed(2);
      setOk(true);
      setMsg(t('common:promoSuccess', { amount }));
      setHistory(await apiGet<HistoryRow[]>('/api/v1/bonuses/history'));
    } catch (err) {
      setMsg(promoMessage(err));
    }
  }

  return (
    <div className="page">
      <div className="section-head">
        <div>
          <h2>{t('nav:bonuses')}</h2>
          <p>{t('common:promoFallbackSubtitle')}</p>
        </div>
      </div>

      <div className="chips promo-filters" role="tablist" aria-label={t('nav:bonuses')}>
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filter === f}
            className={`chip${filter === f ? ' active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {t(`common:promoFilter_${f}`, { defaultValue: f.charAt(0).toUpperCase() + f.slice(1) })}
          </button>
        ))}
      </div>

      {campaigns.length === 0 ? (
        <article className="promo-feature promo-feature-hero">
          <p className="eyebrow">{t('common:brand')}</p>
          <h3>{t('common:promoFallbackTitle')}</h3>
          <p>{t('common:promoFallbackSubtitle')}</p>
          <Link to="/cashier?tab=deposit" className="btn btn-primary btn-play">
            {t('common:promoFallbackCta')}
          </Link>
        </article>
      ) : (
        <>
          {featured ? (
            <article className="promo-feature promo-feature-hero">
              <p className="eyebrow">{featured.campaign_type}</p>
              <h3>{featured.title}</h3>
              <p>{featured.description}</p>
              <strong className="promo-reward">{(featured.reward_minor / 100).toFixed(2)} AZN</strong>
              <p className="muted-note">{featured.code}</p>
            </article>
          ) : null}
          <div className="promo-list">
            {rest.map((c) => (
              <article key={c.code} className="promo-row">
                <div>
                  <strong>{c.title}</strong>
                  <span className="muted-note">{c.campaign_type}</span>
                  <p>{c.description}</p>
                </div>
                <div className="promo-reward">{(c.reward_minor / 100).toFixed(2)} AZN</div>
              </article>
            ))}
          </div>
        </>
      )}

      <form className="panel-block" onSubmit={(e) => void onRedeem(e)}>
        <label htmlFor="promo">{t('common:promoCode')}</label>
        <input id="promo" className="field-input" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
        <button type="submit" className="btn btn-primary" disabled={!user}>
          {t('common:redeem')}
        </button>
        {msg ? <p className={ok ? 'muted-note' : 'error-note'}>{msg}</p> : null}
      </form>

      {history.length > 0 ? (
        <section className="panel-block">
          <h3>{t('common:bonusHistory')}</h3>
          <div className="promo-list">
            {history.map((row) => (
              <article key={`${row.code}-${row.created_at}`} className="promo-row">
                <div>
                  <strong>{row.code}</strong>
                  <span className="muted-note">{row.title}</span>
                </div>
                <div className="promo-reward">+{(row.amount_minor / 100).toFixed(2)} AZN</div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <BonusSelectSheet
        campaigns={campaigns}
        open={sheetOpen}
        onClose={closeSheet}
        onContinue={onSheetContinue}
      />
    </div>
  );
}
